import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

// CSS Imports
import "./index.css";

// REDUX Imports
import { setShowReloginModal } from "../../../redux/features/login/login";
import {
  disabledEnabledError,
  disableTotp,
  initializeTotp,
  removeInitialError,
  resetDisabled,
} from "../../../redux/features/totp/totp";

// COMPONENTS Imports
import TotpBackupCodes from "../../../components/Totp/TotpBackupCodes";
import TotpDownloadApp from "../../../components/Totp/TotpDownloadApp";
import TotpScanner from "../../../components/Totp/TotpScanner";
import TotpVerify from "../../../components/Totp/TotpVerify";
import PrimaryStepper from "../../../components/_utils/CommonSteppers/PrimaryStepper";
import PrimaryButton from "../../../components/_utils/CommonButtons/PrimaryButton";
import SmallPopup from "../../../components/_utils/CommonSmallPopup";
import SVGComponent from "../../../components/_utils/SVGComponent";
import { GlobalConfiguration } from "../../../utils/configurationProvider";

const TotpCreate = () => {
  // useDispatch
  const dispatch = useDispatch();

  // useNavigate
  const navigate = useNavigate();
  const location = useLocation();

  // useState
  const [activeStep, setActiveStep] = useState(0);
  const [openDisabledPop, setOpenDisabledPop] = useState(false);
  const [hashKey, setHashKey] = useState({
    email: "",
    key: "",
  });
  const [isAppDownload, setIsAppDownload] = useState(false);
  const [isQrScanned, setIsQrScanned] = useState(false);
  const [isFocus, setIsFocus] = useState(false);
  const [totp, setTotp] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [open, setOpen] = useState(false);
  const [openScannerCode, setOpenScannerCode] = useState(false);
  const [currentDisplay, setCurrentDisplay] = useState("");
  const [tooltipText, setTooltipText] = useState("Click to copy");
  const [tooltiphashText, setTooltiphashText] = useState("Click to copy");
  const [openAccordion, setOpenAccordion] = useState(false);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [isManualFlow, setIsManualFlow] = useState(false);
  const manualFlowRef = useRef(false);

  // useSelector
  const { enableTotpLoading, enableTotpData, enableTotpError } = useSelector(
    (state) => state.totp,
  );
  const { disableTotpLoading, disableTotpData, disableTotpError } = useSelector(
    (state) => state.totp,
  );
  const {
    initializeTotpLoading,
    initializeTotpData,
    initializeTotpError,
    initializeTotpErrorMsg,
  } = useSelector((state) => state.totp);

  // useCallback
  const ensureSession = useCallback(async () => {
    const token = sessionStorage.getItem("q2p_token");
    const sessionStatus = sessionStorage.getItem("sessionStatus");
    if (!token || sessionStatus === "false") {
      sessionStorage.removeItem("loggedIn");
      if (!sessionStorage.getItem("previousLog")) {
        sessionStorage.setItem("previousLog", location?.pathname || "/home");
      }
      dispatch(setShowReloginModal());
      navigate("/relogin", { replace: true });
      return false;
    }
    return true;
  }, [dispatch, navigate, location?.pathname]);

  const downloadTxtFile = () => {
    const formattedText = backupCodes.join("\n");
    const blob = new Blob([formattedText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "backupCodes.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const steps = [
    "Download app",
    "Scan QR code or enter passkey",
    "Verify authentication code",
    "Download backup codes",
  ];

  let userId;
  try {
    const storedResponse = sessionStorage.getItem("response");
    userId = storedResponse
      ? JSON.parse(storedResponse)?.employees?.id
      : undefined;
  } catch {
    userId = undefined;
  }

  // useEffect
  useEffect(() => {
    dispatch(disabledEnabledError());
  }, [dispatch]);

  useEffect(() => {
    ensureSession();
    const handlePopState = () => {
      ensureSession();
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [ensureSession]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    if (enableTotpData?.status === true) {
      window.addEventListener("beforeunload", handleBeforeUnload);
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [enableTotpData?.status]);

  useEffect(() => {
    let isMounted = true;
    const initTotp = async () => {
      if (hashKey?.key?.length !== 0 || initializeTotpData !== null) return;
      if (!userId) return;

      const ok = await ensureSession();
      if (!ok || !isMounted) return;

      dispatch(
        initializeTotp({
          id: userId,
          type: "Render",
        }),
      );
    };

    initTotp();

    return () => {
      isMounted = false;
    };
  }, [
    ensureSession,
    hashKey?.key?.length,
    initializeTotpData,
    dispatch,
    userId,
  ]);

  useEffect(() => {
    return () => {
      dispatch(removeInitialError());
      dispatch(resetDisabled());
    };
  }, [dispatch]);

  useEffect(() => {
    const refreshTotp = async () => {
      if (
        disableTotpLoading ||
        !disableTotpData?.status ||
        disableTotpError === true ||
        initializeTotpData !== null
      ) {
        return;
      }
      if (!userId) return;

      const ok = await ensureSession();
      if (!ok) return;

      dispatch(initializeTotp({ id: userId, type: "Render" }));
    };

    refreshTotp();
  }, [
    disableTotpLoading,
    disableTotpData,
    disableTotpError,
    initializeTotpData,
    ensureSession,
    dispatch,
    userId,
  ]);

  useEffect(() => {
    if (
      currentDisplay === "steps" ||
      isManualFlow ||
      manualFlowRef.current ||
      openDisabledPop
    )
      return;

    if (disableTotpData?.status === true) {
      setCurrentDisplay("enabled");
      return;
    }

    if (enableTotpData?.status === true) {
      setCurrentDisplay("disabled");
      return;
    }

    if (!initializeTotpLoading && initializeTotpData?.status === true) {
      setCurrentDisplay("enabled");
      setHashKey({
        ...hashKey,
        key: initializeTotpData?.data?.totp_secret,
        email: initializeTotpData?.data?.email,
      });
    } else if (
      initializeTotpError &&
      initializeTotpErrorMsg === "Two Factor Authentication is already enabled"
    ) {
      setCurrentDisplay("disabled");
    }
  }, [
    currentDisplay,
    enableTotpData,
    disableTotpData,
    initializeTotpData,
    initializeTotpError,
    disableTotpError,
    initializeTotpLoading,
    initializeTotpErrorMsg,
    hashKey,
    isManualFlow,
    openDisabledPop,
  ]);

  const handleNext = () => {
    setActiveStep((x) => x + 1);
  };

  const renderTotp = () => {
    switch (activeStep) {
      case 0:
        return (
          <TotpDownloadApp
            isAppDownload={isAppDownload}
            setIsAppDownload={setIsAppDownload}
            isScannerOpen={isScannerOpen}
            setIsScannerOpen={setIsScannerOpen}
          />
        );
      case 1:
        return (
          <TotpScanner
            hashKey={hashKey}
            isQrScanned={isQrScanned}
            setIsQrScanned={setIsQrScanned}
            open={open}
            setOpen={setOpen}
            tooltipText={tooltipText}
            setTooltipText={setTooltipText}
          />
        );
      case 2:
        return (
          <TotpVerify
            isFocus={isFocus}
            setIsFocus={setIsFocus}
            totp={totp}
            setTotp={setTotp}
            userId={userId}
            setBackupCodes={setBackupCodes}
            enableTotpLoading={enableTotpLoading}
            enableTotpData={enableTotpData}
            enableTotpError={enableTotpError}
            handleNext={handleNext}
          />
        );
      case 3:
        return (
          <TotpBackupCodes
            backupCodes={backupCodes}
            open={open}
            setOpen={setOpen}
            tooltipText={tooltipText}
            setTooltipText={setTooltipText}
            openAccordion={openAccordion}
            setOpenAccordion={setOpenAccordion}
            hashKey={hashKey}
            openScannerCode={openScannerCode}
            setOpenScannerCode={setOpenScannerCode}
            tooltiphashText={tooltiphashText}
            setTooltiphashText={setTooltiphashText}
          />
        );
      default:
        return null;
    }
  };

  console.log("xyz", isAppDownload);
  return (
    <>
      <div className="h-navbar-breadcrumb-height hidden Qmd:block Qlg:bg-transparent bg-primary-nav"></div>
      <div
        className={`p-padding-sm fixed bg-primary-nav w-full flex Qmd:hidden flex-row justify-start items-center gap-gap-md  shadow-box-shadow-bottom `}
        style={{
          opacity: isScannerOpen ? "var(--dark-opacity)" : "",
          zIndex: isScannerOpen ? "" : "z-z-index-fixed-ui",
        }}
      >
        <div
          className="flex items-center gap-gap-md cursor-pointer"
          onClick={async () => {
            const ok = await ensureSession();
            if (ok) navigate(-1);
          }}
        >
          <SVGComponent
            url={
              "https://q2pay.in/new_assets/common/arrows/arrow_left_white.svg"
            }
            color="var(--text-primary)"
            height={"var(--icon-md)"}
            width={"var(--icon-md)"}
          />
        </div>
        <div className="flex flex-col text-text-primary text-font-md gap-y-gap-xs">
          {currentDisplay === "steps" ? (
            <div>TOTP set up</div>
          ) : (
            <div>Settings</div>
          )}
          {currentDisplay === "steps" ? (
            <div className="text-font-xs text-text-secondary">
              {steps[activeStep]}
            </div>
          ) : (
            <div className="text-font-xs text-text-secondary">TOTP</div>
          )}
        </div>
      </div>
      <div>
        {openDisabledPop && (
          <>
            <div
              className="fixed top-top-base left-left-base w-screen h-screen z-z-index-overlay flex justify-center items-center "
              style={{
                backgroundColor: "black",
                opacity: "var(--dark-opacity)",
              }}
            ></div>
            <div className="fixed top-top-base left-left-base w-screen h-screen z-z-index-overlay flex justify-center items-center ">
              <SmallPopup
                headerTitle="Disabled TOTP"
                headerFontWeight="semibold"
                headerFontColor="text-primary"
                textSize="font-md"
                secondHeaderTitle="Are you sure you want to disable TOTP?"
                secondHeaderFontWeight="semibold"
                secondHeaderFontColor="text-primary"
                secondHeaderTextSize="font-sm"
                btnOneText="Cancel"
                btnOneHandleFunction={() => setOpenDisabledPop(false)}
                btnOneEnabledBgColor="var(--primary-bg)"
                btnOneOutLineColor="var(--text-primary)"
                btnOneEnabledTextColor="var(--text-primary)"
                btnOneIsDisabled={false}
                btnOneHoverOption="white"
                btnTwoText="Disable"
                noteText="The  two-factor authentication using TOTP will become inactive."
                btnTwoHandleFunction={(e) => {
                  dispatch(removeInitialError());
                  dispatch(
                    disableTotp({
                      id: userId,
                      setCurrentDisplay: setCurrentDisplay,
                      setOpenDisabledPop: setOpenDisabledPop,
                    }),
                  );
                }}
                btnTwoEnabledBgColor="var(--positive-act)"
                btnTwoEnabledTextColor="var(--text-primary)"
                btnTwoIsDisabled={disableTotpLoading ? true : false}
                btnTwoDisabledBgColor="var(--text-disabled)"
                btnTwoIsLoaderNeeded={true}
                btnTwoIsLoading={disableTotpLoading}
                btnTwoHoverOption="black"
              />
            </div>
          </>
        )}
      </div>
      <div
        className={`w-full flex flex-col items-center bg-primary-bg gap-gap-sm Qsmd:gap-gap-md ${isScannerOpen && "overflow-hidden"} flex-grow flex-1 totp-main-big-div`}
      >
        {currentDisplay === "steps" && (
          <>
            <div
              style={{
                height: "calc(var(--fixed-header) - var(--gap-md))",
                flexShrink: GlobalConfiguration.lengthCheckZero,
              }}
            ></div>
            <div className="fixed w-full flex justify-center bottom-bottom-base Qmd:bottom-auto top-auto Qmd:top-[(var(--navbar-breadcrumb-height))] z-z-index-content min-h-stepper-height">
              <PrimaryStepper
                activeStep={activeStep}
                handleBackwardAction={(e) => {
                  if (activeStep === GlobalConfiguration.lengthCheckZero) {
                    setIsManualFlow(false);
                    manualFlowRef.current = false;
                    setCurrentDisplay("enabled");
                  } else
                    setActiveStep((x) =>
                      Math.max(
                        x - GlobalConfiguration.lengthCheckOne,
                        GlobalConfiguration.lengthCheckZero,
                      ),
                    );
                }}
                handleForwardAction={(e) => {
                  if (activeStep === GlobalConfiguration.lengthCheckThree) {
                    downloadTxtFile();
                    setIsManualFlow(false);
                    manualFlowRef.current = false;
                    setCurrentDisplay("disabled");
                    return;
                  } else
                    setActiveStep(
                      (x) => x + GlobalConfiguration.lengthCheckOne,
                    );
                }}
                dataArray={[
                  {
                    previousText: "Cancel",
                    nextText: "Next",
                    previousEnableBgColor: "var(--primary-nav-act)",
                    nextEnableBgColor: "var(--text-primary)",
                    nextEnableTextColor: "var(--primary-bg)",
                    nextHoverOption: "black",
                    previousDisableBgColor: "var(--primary-nav-act)",
                    nextDisableBgColor: "var(--text-disabled)",
                    nextIsLoading: false,
                    nextIsEnabled: isAppDownload,
                    stepperText: "Download app",
                  },
                  {
                    previousText: "Previous",
                    nextText: "Next",
                    previousEnableBgColor: "var(--primary-nav-act)",
                    nextEnableBgColor: "var(--text-primary)",
                    nextEnableTextColor: "var(--primary-bg)",
                    nextHoverOption: "black",
                    previousDisableBgColor: "var(--primary-nav-act)",
                    nextDisableBgColor: "var(--text-disabled)",
                    nextIsLoading: false,
                    nextIsEnabled: isQrScanned,
                    stepperText: "Scan QR code or enter passkey",
                  },
                  {
                    previousText: "Previous",
                    nextText: "Next",
                    previousEnableBgColor: "var(--primary-nav-act)",
                    nextEnableBgColor: "var(--text-primary)",
                    nextEnableTextColor: "var(--primary-bg)",
                    nextHoverOption: "black",
                    previousDisableBgColor: "var(--primary-nav-act)",
                    nextDisableBgColor: "var(--text-disabled)",
                    nextIsLoading: false,
                    nextIsEnabled: !!backupCodes.length,
                    stepperText: "Verify authentication code",
                  },
                  {
                    previousText: "Previous",
                    nextText: "Download",
                    previousEnableBgColor: "var(--primary-nav-act)",
                    nextEnableBgColor: "var(--positive-act)",
                    nextEnableTextColor: "var(--text-primary)",
                    nextHoverOption: "black",
                    previousDisableBgColor: "var(--primary-nav-act)",
                    nextDisableBgColor: "var(--text-disabled)",
                    nextIsLoading: false,
                    nextIsEnabled: true,
                    stepperText: "Download backup codes",
                  },
                ]}
              />
            </div>
            <div className="Qlg:w-std-width w-full h-full flex-grow Qmd:flex hidden Qlg:pt-padding-lg">
              {renderTotp()}
            </div>
            <div className="Qlg:w-std-width w-full h-full flex-grow  Qmd:hidden pt-padding-base">
              {renderTotp()}
              <div
                style={{
                  height: "var(--min-height-secondary-edge-fill-btn)",
                }}
              ></div>
            </div>
          </>
        )}
        {currentDisplay === "enabled" && (
          <>
            <div
              className="Qmd:hidden block"
              style={{
                height: "calc(var(--fixed-header) - var(--gap-md))",
              }}
            ></div>
            <div className="flex flex-col justify-start items-center gap-gap-md w-full">
              <div className="bg-primary-nav w-full py-padding-md flex justify-center px-padding-md">
                <div className="w-std-width text-text-primary flex flex-col gap-y-gap-xs">
                  <div className="font-semibold text-font-sm">TOTP</div>
                  <div className="text-font-xs text-text-secondary">
                    Protect your account by enabling two-factor authentication
                    (2FA) using TOTP (Time-based One-time Password). This adds
                    an extra layer of security to your login process.
                  </div>
                </div>
              </div>
              <div className="bg-primary-card Qlg:w-std-width w-width-almost-full p-padding-md  rounded-border-radius-md flex flex-col gap-gap-md Qmd:mx-margin-base mx-margin-md">
                <div className="text-text-primary flex flex-col gap-y-gap-xs">
                  <div className="text-font-md font-semibold">Enable TOTP</div>
                  <div className="text-font-xs text-text-secondary">
                    To enable TOTP, complete the setup process.
                  </div>
                </div>
                <div className="w-auto h-auto">
                  <PrimaryButton
                    btnText="Set TOTP"
                    btnType="button"
                    enabledBgColor="var(--positive-act)"
                    hoverOption="black"
                    handleFunction={(e) => {
                      manualFlowRef.current = true;
                      setIsManualFlow(true);
                      setCurrentDisplay("steps");
                    }}
                    enabledTextColor="var(--text-primary)"
                  />
                </div>
                <div className="text-text-secondary text-font-xs">
                  Note - You will need to download an Authentication application
                  on your mobile, tablet or desktop device. It will generate a
                  code that you wll need to enter when you log in.
                </div>
              </div>
              <SVGComponent
                url={
                  "https://q2pay.in/new_assets/graphics/totp/backup_codes.svg"
                }
                height={"var(--graphic-square-height-md)"}
                width={"var(--graphic-width-md)"}
              />
            </div>
          </>
        )}
        {currentDisplay === "disabled" && (
          <>
            <div
              className="Qmd:hidden block"
              style={{
                height: "calc(var(--fixed-header) - var(--gap-md))",
              }}
            ></div>
            <div className="flex flex-col justify-start items-center gap-gap-md w-full">
              <div className="bg-primary-nav w-full py-padding-md flex justify-center px-padding-md">
                <div className="w-std-width text-text-primary flex flex-col gap-y-gap-xs">
                  <div className="font-semibold text-font-sm">TOTP</div>
                  <div className="text-font-xs text-text-secondary">
                    Protect your account by enabling two-factor authentication
                    (2FA) using TOTP (Time-based One-time Password). This adds
                    an extra layer of security to your login process.
                  </div>
                </div>
              </div>
              <div className="w-full Qlg:w-std-width flex flex-col-reverse Qmd:flex-row gap-gap-md">
                <div className="flex flex-col justify-center items-center gap-gap-md Qsmd:w-width-md">
                  <SVGComponent
                    url={
                      "https://q2pay.in/new_assets/graphics/totp/totp_enabled.svg"
                    }
                    height={"var(--graphic-square-height-md)"}
                    width={"var(--graphic-width-md)"}
                  />
                  <div className="text-text-primary font-semibold text-font-lg">
                    TOTP has been enabled.
                  </div>
                </div>
                <div className="mx-margin-md Qsmd:w-width-md">
                  <div className="bg-primary-card rounded-border-radius-md p-padding-md flex flex-col gap-gap-md Qmd:w-auto w-full">
                    <div className="font-semibold text-text-primary text-font-md">
                      Two-Factor Authentication is Now{" "}
                      <span className="text-settled">Active!</span>
                    </div>
                    <div className="text-text-secondary text-font-xs">
                      Your account is now protected with Time-Based One-Time
                      Password (TOTP) verification. This adds an extra layer of
                      security every time you log in keeping your information
                      safer than ever.
                    </div>
                    <div className="text-text-secondary text-font-xs">
                      To generate new backup codes or re-register your devices,
                      first disable TOTP and repeat the setup process.{" "}
                      <span
                        className="text-text-primary hover:text-failed underline font-semibold text-font-xs cursor-pointer"
                        onClick={() => {
                          setOpenDisabledPop(true);
                          dispatch(disabledEnabledError());
                          setIsAppDownload(false);
                          setActiveStep(0);
                          setIsQrScanned(false);
                          setIsFocus(false);
                          setTotp("");
                          setHashKey({
                            email: "",
                            key: "",
                          });
                        }}
                      >
                        Disable TOTP
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default TotpCreate;
